import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { EventStatus, EventView } from '@cuito/types';
import { Button, DateListField, Input, normalizeDates } from '@cuito/ui';
import { eventsApi } from '../api/events';
import { readServerMessage } from '../lib/apiError';
import { useAuthStore } from '../stores/authStore';

/**
 * 展会列表（`05-ui-design-system.md` §3.2 的 `/events`）—— **社团展会存档**。
 *
 * ## 这一页只有一个数据来源：`GET /events` 的裸数组
 *
 * 契约 §11.8：该端点**只返回 `source = TENANT` 且 `tenantId = 我` 的行**。
 * 因此本页**绝不**去读 `GET /participations` 或 `GET /events/available` 来"补"行 ——
 * 报名审核通过但**未缴费**的平台展会**不在这份列表里**（它只在「我报名的展会」页），
 * 只有缴费审核通过（`PAID`）时服务端创建的存档才会出现。
 * 页面测试用一个「`/participations` 里有一条 APPROVED 未缴费记录」的夹具钉住这一点：
 * 列表里**不会**因此多出一行，而且 `participationsApi` **一次都没被调用**。
 *
 * ## 状态流转只发请求，不判合法性
 *
 * `PREPARING → ONGOING → ENDED` 的合法性在服务端（§11.7，非法迁移是 `409`）。
 * 前端只按当前 `status` 渲染下一个动作按钮，**不**在本地短路。
 *
 * ## 日期走日期选择器，不走文字输入
 *
 * `dates` 是"一天一行"的数组（契约 §11.1：非空、元素 `YYYY-MM-DD`）。文字输入要求用户背
 * 格式（`2026-08-15,2026-08-16`），而写歪的代价不在本地：服务端 `@IsDateString()` 直接
 * `400`，结算侧另有一条严格到 `YYYY-MM-DD` 的形状检查（非法即 `422`）。改走
 * `DateListField`（原生 `<input type="date">`）之后，格式问题在控件这一层就不存在了；
 * "行 → 请求体"的转换统一走 `normalizeDates()`（去空行 / 去重 / 升序的唯一实现）。
 *
 * 提交前的闸门只有两条：名称非空、至少选了一个日期（`请填写展会日期`，与平台端
 * 主办方发布展会逐字同款）。其余仍由服务端裁决。
 */
const STATUS_LABELS: Record<EventStatus, string> = {
  PREPARING: '筹备中',
  ONGOING: '进行中',
  ENDED: '已结束',
};

/** 当前状态 → 下一个状态（`null` = 终态，没有可点的动作） */
const NEXT_STATUS: Record<EventStatus, EventStatus | null> = {
  PREPARING: 'ONGOING',
  ONGOING: 'ENDED',
  ENDED: null,
};

const ACTION_LABELS: Record<string, string> = {
  ONGOING: '开始展会',
  ENDED: '结束展会',
};

export default function EventsPage() {
  const canWrite = useAuthStore((s) => s.hasPermission('event:write'));

  const [rows, setRows] = useState<EventView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [venue, setVenue] = useState('');
  const [dateRows, setDateRows] = useState<string[]>(['']);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setRows(await eventsApi.list());
    } catch (e) {
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (name.trim() === '') {
      setFormError('请填写展会名称');
      return;
    }
    const dates = normalizeDates(dateRows);
    if (dates.length === 0) {
      setFormError('请填写展会日期');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await eventsApi.create({ name: name.trim(), venue: venue.trim(), dates });
      setName('');
      setVenue('');
      setDateRows(['']);
      setFormOpen(false);
      await load();
    } catch (e) {
      setFormError(readServerMessage(e) ?? '保存失败，请重试');
    } finally {
      setSubmitting(false);
    }
  }

  async function runStatus(row: EventView): Promise<void> {
    const next = NEXT_STATUS[row.status];
    if (next === null) return;
    setBusyId(row.id);
    setActionError(null);
    try {
      await eventsApi.setStatus(row.id, next);
      await load();
    } catch (e) {
      setActionError(readServerMessage(e) ?? '保存失败，请重试');
    } finally {
      setBusyId(null);
    }
  }

  async function runDelete(id: string): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      await eventsApi.remove(id);
      setConfirmingId(null);
      await load();
    } catch (e) {
      setActionError(readServerMessage(e) ?? '保存失败，请重试');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="max-w-7xl" data-testid="events-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">展会</h1>
        {canWrite && (
          <Button data-testid="event-create-open" onClick={() => setFormOpen((open) => !open)}>
            新建展会
          </Button>
        )}
      </div>

      {formOpen && (
        <form
          onSubmit={handleCreate}
          className="mb-6 flex flex-col gap-3 rounded-lg border-2 border-black p-4"
        >
          <Input
            label="展会名称"
            data-testid="event-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            label="场馆"
            data-testid="event-venue"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
          />
          <DateListField
            label="展会日期"
            testIdPrefix="event"
            value={dateRows}
            onChange={setDateRows}
          />
          {formError !== null && (
            <p className="text-sm font-bold text-red-600" data-testid="events-error">
              {formError}
            </p>
          )}
          <Button type="submit" data-testid="event-submit" disabled={submitting}>
            {submitting ? '创建中…' : '创建展会'}
          </Button>
        </form>
      )}

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error !== null && (
        <p className="font-bold text-red-600" data-testid="events-error">
          {error}
        </p>
      )}
      {actionError !== null && (
        <p className="mb-3 font-bold text-red-600" data-testid="events-error">
          {actionError}
        </p>
      )}
      {!loading && error === null && rows.length === 0 && <p className="text-gray-500">暂无展会</p>}

      {!loading && error === null && rows.length > 0 && (
        <table className="w-full rounded-lg border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">展会</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">场馆</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">日期</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">摊位号</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">状态</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">地点码</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">订单数</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} data-testid="event-row" data-event-id={row.id} data-status={row.status}>
                <td className="border-b border-gray-200 px-4 py-3 text-sm font-bold">{row.name}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.venue}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.dates.join('、')}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.stallNumber ?? '—'}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm" data-field="event-status">
                  {STATUS_LABELS[row.status]}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.location.code}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.orderCount}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    {canWrite && NEXT_STATUS[row.status] !== null && (
                      <Button
                        size="sm"
                        variant="secondary"
                        data-field="event-status-action"
                        disabled={busyId === row.id}
                        onClick={() => void runStatus(row)}
                      >
                        {ACTION_LABELS[NEXT_STATUS[row.status] ?? ''] ?? ''}
                      </Button>
                    )}
                    {canWrite && confirmingId !== row.id && (
                      <Button
                        size="sm"
                        variant="danger"
                        data-testid="event-delete"
                        disabled={busyId === row.id}
                        onClick={() => setConfirmingId(row.id)}
                      >
                        删除
                      </Button>
                    )}
                    {canWrite && confirmingId === row.id && (
                      <>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={busyId === row.id}
                          onClick={() => void runDelete(row.id)}
                        >
                          确认
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setConfirmingId(null)}
                        >
                          取消
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
